<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { getLocalData, setLocalData, removeLocalData } from '@/utils/localdata';
import { validateRegisterData } from '@/utils/register.js';
import { useI18n } from 'vue-i18n';
import axios from '@/plugins/axios.js';
import { useAlerts } from '@/store/alerts.js'
import phoneCode from '@/views/components/register/phoneCode.vue'
import Phone from '@/views/components/Phone.vue'
import KiwishLoader from '@/views/components/KiwishLoader.vue';
import Contact from '@/views/components/Contact.vue';
import PasswordToggle from '@/views/components/PasswordToggle.vue';
import { pushGTMEvent } from '@/utils/gtm';

const { locale } = useI18n();
const router = useRouter();
const viewContactModal = ref(false);
const APP_NAME = process.env.VUE_APP_NAME;
const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;
const VUE_WEB_URL = process.env.VUE_APP_WEB_URL;
const step = ref(1);
const sendingPhoneCode = ref(false);
const checkingPhoneCode = ref(false);
const registering = ref(false);
const phoneCodeValue = ref('');
const nameError = ref('');
const emailError = ref('');
const passError = ref('');
const policyError = ref('');
const lang = ref();
const sendedCode = ref(false);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const userData = ref({
  gender: '',
  age_range: '',
  prefix: '',
  phone: '',
  name: '',
  email: '',
  pass: '',
  pass_confirm: '',
  policy: 0,
  newsletter: 0,
});

// Variable para guardar la altura inicial del viewport
let initialViewportHeight = null;

// Función para ajustar la altura del viewport considerando Safari iOS
// IMPORTANTE: Solo se establece una vez al cargar, NO cuando aparece el teclado
const setViewportHeight = () => {
  // Si ya tenemos una altura inicial y no fue un cambio de orientación, no recalcular
  // Esto evita que el contenido se encoja cuando aparece el teclado
  if (initialViewportHeight !== null) return;

  const vh = window.innerHeight * 0.01;
  initialViewportHeight = vh;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

// Función específica para cambio de orientación (aquí sí recalculamos)
const handleOrientationChange = () => {
  // Resetear para permitir recalcular
  initialViewportHeight = null;
  // Esperar a que el navegador complete la rotación
  setTimeout(() => {
    setViewportHeight();
  }, 100);
};

onMounted(async() => {
  // Configurar altura de viewport para Safari iOS (solo una vez)
  setViewportHeight();

  // Solo escuchar cambio de orientación (NO resize ni visualViewport)
  // El resize del visualViewport es lo que causa el problema con el teclado
  window.addEventListener('orientationchange', handleOrientationChange);

  setLocalData('intro', 'true');
  lang.value = await getLocalData('lang')

  // Recuperar el paso guardado
  const savedStep = await getLocalData('registerStep');
  if (savedStep) {
    step.value = parseInt(savedStep);
  }

  let dataTemp = await getLocalData('registerData');
  if (dataTemp){
    try {
      const parsedData = JSON.parse(dataTemp);
      // Merge con valores por defecto para asegurar que todos los campos existan
      userData.value = {
        gender: parsedData.gender || '',
        age_range: parsedData.age_range || '',
        prefix: parsedData.prefix || '',
        phone: parsedData.phone || '',
        name: '',
        email: '',
        pass: '',
        pass_confirm: '',
        policy: 0,
        newsletter: parsedData.newsletter || 0,
      };
      // Solo restaurar campos sensibles si estamos en el último paso
      if (step.value >= 5) {
        userData.value.name = parsedData.name || '';
        userData.value.email = parsedData.email || '';
        userData.value.pass = parsedData.pass || '';
        userData.value.pass_confirm = parsedData.pass_confirm || '';
        userData.value.policy = parsedData.policy || 0;
      }
    } catch (e) {
      // Error parsing data, usar valores por defecto
      console.error('Error parsing registerData:', e);
    }
  }
  // Enviar evento inicial a GTM (espera automáticamente a que GTM esté listo)
  if (step.value == 1) {
    pushGTMEvent('register_start');
  }
});

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('orientationchange', handleOrientationChange);
});

const saveDataTemp = () => {
  // Guardar datos del usuario
  setLocalData('registerData', JSON.stringify(userData.value));
  // Guardar el paso actual
  setLocalData('registerStep', step.value.toString());
}

// Guardar automáticamente los datos del paso 5 mientras el usuario escribe
const saveStep5Data = () => {
  if (step.value === 5) {
    saveDataTemp();
  }
}

const registerTGM = (eventTo) => {
  pushGTMEvent(eventTo);
}

// Veo cuando cambia de paso y registro el evento en dataLayer
watch(step, (newStep) => {
  // Guardar el paso actual cada vez que cambie
  setLocalData('registerStep', newStep.toString());

  if (newStep === 2) {
    registerTGM('register_age_range');
  } else if (newStep === 3) {
    registerTGM('register_phone');
  } else if (newStep === 4) {
    registerTGM('register_phone_code');
  } else if (newStep === 5) {
    registerTGM('register_user_data');
  } else if (newStep === 6) {
    registerTGM('register_end');
  }
});

const registerPhone = () => {
  userData.value.lang = lang.value;
  // Asegurar que el teléfono no tenga espacios ni caracteres extraños
  userData.value.phone = userData.value.phone.replace(/\D/g, '');
  setLocalData('prefix', userData.value.prefix);
  // Guardar datos antes de enviar el código
  saveDataTemp();
  sendingPhoneCode.value = true;
  axios.post('/register/phone-step', userData.value)
    .then (async r => {
      sendingPhoneCode.value = false;
      await nextTick();
      if (r.status >= 200 && r.status < 300) {
        step.value++;
      }else{
        useAlerts().alert = {visible: true, message: r.data.exception ? 'register.msg.error.general' : r.data.message, class: 'alert-error'}
      }
    }).catch(e => {
      sendingPhoneCode.value = false;
      useAlerts().alert = {visible: true, message: e.response?.data?.exception ? 'register.msg.error.general' : e.response?.data?.message, class: 'alert-error'}
    })
}
const resendCode = () => {
  sendingPhoneCode.value = true;
  userData.value.lang = lang.value;
  // Asegurar que el teléfono no tenga espacios
  userData.value.phone = userData.value.phone.replace(/\D/g, '');
  axios.post('/register/resend-code', userData.value)
    .then (async r => {
      sendingPhoneCode.value = false;
      await nextTick();
      if (r.status >= 200 && r.status < 300) {
        useAlerts().alert = {visible: true, message: 'register.phonecode.resend.msg', class: 'alert-success'}
        sendedCode.value = true;
      }else{
        useAlerts().alert = {visible: true, message: r.data.exception ? 'register.msg.error.general' : r.data.message, class: 'alert-error'}
      }
    }).catch(e => {
      sendingPhoneCode.value = false;
      useAlerts().alert = {visible: true, message: e.response?.data?.exception ? 'register.msg.error.general' : e.response?.data?.message, class: 'alert-error'}
    })
}

const checkPhoneCode = () => {
  checkingPhoneCode.value = true;
  // Asegurar que el teléfono no tenga espacios
  const cleanPhone = userData.value.phone.replace(/\D/g, '');
  let data = {prefix: userData.value.prefix, phone: cleanPhone, code: phoneCodeValue.value, lang: lang.value};
  axios.post('/register/check-code', data)
    .then (async r => {
      checkingPhoneCode.value = false;
      await nextTick();
      if (r.status >= 200 && r.status < 300) {
        useAlerts().alert = {visible: true, message: 'register.phonecode.ok', class: 'alert-success'}
        step.value = 5;
      }else{
        useAlerts().alert = {visible: true, message: r.data.exception ? 'register.msg.error.general' : r.data.message, class: 'alert-error'}
      }
    }).catch(e => {
      checkingPhoneCode.value = false;
      useAlerts().alert = {visible: true, message: e.response?.data?.exception ? 'register.msg.error.general' : e.response?.data?.message, class: 'alert-error'}
    })
}

const registerUser = () => {
  // Prevenir ejecuciones múltiples
  if (registering.value) {
    return;
  }

  let validation = validateRegisterData(userData.value);
  if (validation.name === true && validation.pass === true && validation.pass_confirm === true && validation.policy === true){
    //&& validation.email === true
    save();
//
  }else{
    useAlerts().alert = {visible: true, message: 'msg.form.error', class: 'alert-error'}
    if (!validation.name) nameError.value = 'msg.form.field.required';
    // if (!validation.email) emailError.value = 'register.msg.error.form.email';
    if (!validation.pass) passError.value = 'register.msg.error.form.pass';
    if (!validation.pass_confirm) passError.value = 'register.msg.error.form.pass_confirm';
    if (!validation.policy) policyError.value = 'register.msg.error.form.policy';
  }
}

// Validación en tiempo real (onBlur)
const validateName = () => {
  if (!userData.value.name || userData.value.name.trim() === '') {
    nameError.value = 'msg.form.field.required';
  }
};

const validatePass = () => {
  if (userData.value.pass && userData.value.pass.length < 8) {
    passError.value = 'register.msg.error.form.pass';
  }
};

const validatePassConfirm = () => {
  if (userData.value.pass_confirm && userData.value.pass !== userData.value.pass_confirm) {
    passError.value = 'register.msg.error.form.pass_confirm';
  }
};

const save = () => {
  registering.value = true;
  userData.value.lang = lang.value;
  axios.post('/register/register', userData.value)
    .then (async r => {
      registering.value = false;
      await nextTick();
      if (r.status >= 200 && r.status < 300) {
        useAlerts().alert = {visible: true, message: 'register.ok', class: 'alert-success'}
        step.value++;
        // Limpiar localStorage después de registro exitoso
        await removeLocalData('registerStep');
        await removeLocalData('registerData');
      }else{
        useAlerts().alert = {visible: true, message: r.data.exception ? 'register.msg.error.general' : r.data.message, class: 'alert-error'}
      }
    }).catch(e => {
      registering.value = false;
      useAlerts().alert = {visible: true, message: e.response?.data?.exception ? 'register.msg.error.general' : e.response?.data?.message, class: 'alert-error'}
    })
}
</script>

<template>
  <ion-page class="bg-gradient-secrets">
    <!-- Logo header -->
    <div class="auth-logo-header">
      <img :src="VUE_ASSETS_URL + 'logokw_dark.png'"
           :srcset="VUE_ASSETS_URL + 'logokw_dark.png 1x, ' + VUE_ASSETS_URL + 'logokw_dark@2.png 2x'"
           alt="Kiwish" class="auth-logo" />
    </div>

    <div class="auth-wrapper">
      <div class="auth-container">
        <!-- Step 1: Gender selection -->
        <div v-if="step == 1" class="step-content">
          <div class="gender-options">
            <button class="gender-option" :class="{ 'selected': userData.gender === 'man' }" @click="userData.gender='man'">
              <span>{{ $t('register.gender.man') }}</span>
            </button>

            <button class="gender-option selected-highlight" :class="{ 'selected': userData.gender === 'woman' }" @click="userData.gender='woman'">
              <span>{{ $t('register.gender.woman') }}</span>
            </button>

            <button class="gender-option" :class="{ 'selected': userData.gender === 'neutral' }" @click="userData.gender='neutral'">
              <span>{{ $t('register.gender.neutral') }}</span>
            </button>
          </div>
        </div>

        <!-- Step 2: Age Range -->
        <div v-if="step == 2" class="step-content">
          <h2 class="step-title">{{ $t('register.age.choose') }}</h2>
          <div class="gender-options">
            <button class="gender-option" :class="{ 'selected': userData.age_range === '18_25' }" @click="userData.age_range='18_25'">
              <span>{{ $t('register.age.18_25') }}</span>
            </button>

            <button class="gender-option" :class="{ 'selected': userData.age_range === '26_35' }" @click="userData.age_range='26_35'">
              <span>{{ $t('register.age.26_35') }}</span>
            </button>

            <button class="gender-option" :class="{ 'selected': userData.age_range === '36_45' }" @click="userData.age_range='36_45'">
              <span>{{ $t('register.age.36_45') }}</span>
            </button>

            <button class="gender-option" :class="{ 'selected': userData.age_range === '46_65' }" @click="userData.age_range='46_65'">
              <span>{{ $t('register.age.46_65') }}</span>
            </button>

            <button class="gender-option" :class="{ 'selected': userData.age_range === 'no_say' }" @click="userData.age_range='no_say'">
              <span>{{ $t('register.age.no_say') }}</span>
            </button>
          </div>
        </div>

        <!-- Step 3: Phone -->
        <div v-if="step == 3" class="step-content">
          <div class="auth-input">
            <Phone v-model:phone="userData.phone" v-model:prefix="userData.prefix" />
          </div>
          <KiwishLoader :is-open="sendingPhoneCode" :message="$t('register.phone.sending')" />
        </div>

        <!-- Step 4: Phone verification -->
        <div v-if="step == 4" class="step-content">
          <div class="verification-section">
            <div class="verification-icon">
              <img :src="VUE_ASSETS_URL + 'imgs/icons/padlock.png'" alt="Verification" class="verification-img" />
            </div>
            <h2 class="verification-title">{{ $t('register.phonecode.title') }}</h2>
            <p class="verification-subtitle">{{ $t('register.phonecode.subtitle') }}</p>

            <div class="code-input-container">
              <phoneCode @updateCode="(value) => (phoneCodeValue = value)" />
            </div>

            <KiwishLoader :is-open="sendingPhoneCode" :message="$t('register.phone.sending')" />
            <KiwishLoader :is-open="checkingPhoneCode" :message="$t('register.phonecode.checking')" />

            <div class="resend-container" v-if="!sendedCode">
              <ion-text class="resend-hint">{{ $t('register.phonecode.resend.hint') }}</ion-text>
              <ion-text class="resend-link" @click="resendCode">{{ $t('register.phonecode.resend') }}</ion-text>
            </div>
          </div>
        </div>

        <!-- Step 5: User data -->
        <div v-if="step == 5" class="step-content step-form">
          <div class="auth-input">
            <ion-input type="text" v-model="userData.name"
              @keydown="nameError = ''"
              @keyup="saveStep5Data"
              @keydown.enter.prevent="registerUser"
              @blur="validateName"
              :placeholder="$t('register.field.name')"
              enterkeyhint="next"
            />
            <span class="input-check" v-if="userData.name && !nameError">&#10003;</span>
          </div>
          <ion-text class="hint-text">{{ $t('register.field.name.hint') }}</ion-text>
          <ion-text class="error-text" v-if="nameError != ''">{{ $t(nameError) }}</ion-text>

          <div class="auth-input">
            <ion-input
              v-model="userData.pass"
              @keydown="passError = ''"
              @keyup="saveStep5Data"
              @keydown.enter.prevent="registerUser"
              @blur="validatePass"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('password')"
              enterkeyhint="next"
            />
            <PasswordToggle :visible="showPassword" @toggle="showPassword = !showPassword" />
          </div>
          <ion-text class="error-text" v-if="passError != ''">{{ $t(passError) }}</ion-text>

          <div class="auth-input">
            <ion-input
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model="userData.pass_confirm"
              @keydown="passError = ''"
              @keyup="saveStep5Data"
              @keydown.enter.prevent="registerUser"
              @blur="validatePassConfirm"
              :placeholder="$t('register.password.confirm')"
              enterkeyhint="go"
            />
            <PasswordToggle :visible="showConfirmPassword" @toggle="showConfirmPassword = !showConfirmPassword" />
          </div>

          <KiwishLoader :is-open="registering" :message="$t('register.registering')" />

          <div class="checkbox-row">
            <ion-checkbox v-model="userData.policy" @click="policyError = ''"></ion-checkbox>
            <ion-text class="checkbox-label">
              {{ $t('register.policy') }}
              <a :href="VUE_WEB_URL+'/politica-privacidad-app'" target="_blank" class="policy-link">{{ $t('register.policy.conditions') }}</a>
              {{ $t('register.policy.and') }}
              <a :href="VUE_WEB_URL+'/aviso-legal'" target="_blank" class="policy-link">{{ $t('register.policy.legal') }}</a>
            </ion-text>
          </div>
          <ion-text class="error-text" v-if="policyError != ''">{{ $t(policyError) }}</ion-text>

          <div class="checkbox-row">
            <ion-checkbox v-model="userData.newsletter"></ion-checkbox>
            <ion-text class="checkbox-label">{{ $t('register.newsletter') }} {{ APP_NAME }}</ion-text>
          </div>
        </div>

        <!-- Step 6: Success -->
        <div v-if="step == 6" class="step-content vertical-center">
          <h2 class="success-title">{{ $t('register.registered.title') }}</h2>
          <p class="success-subtitle">{{ $t('register.registered.subtitle') }}</p>
        </div>

        <!-- Buttons -->
        <div class="button-container">
          <button class="btn-back" @click="step--" v-if="step > 1 && step < 6">
            <ion-icon icon="arrow-back"></ion-icon>
          </button>

          <button class="btn-primary" :class="{ 'btn-full': step === 1 || step === 6 }" v-if="step == 1" :disabled="userData.gender == ''" @click="saveDataTemp();step++">{{ $t('continue') }}</button>
          <button class="btn-primary" v-if="step == 2" :disabled="userData.age_range == ''" @click="saveDataTemp();step++">{{ $t('continue') }}</button>
          <button class="btn-primary" v-if="step == 3" :disabled="userData.phone == '' || userData.phone.length < 3 || userData.prefix == '' || sendingPhoneCode" @click="saveDataTemp();registerPhone()">{{ $t('continue') }}</button>
          <button class="btn-primary" v-if="step == 4" :disabled="phoneCodeValue.length < 4" @click="checkPhoneCode()">{{ $t('continue') }}</button>
          <button class="btn-primary" v-if="step == 5" :disabled="registering" @click="registerUser()">{{ $t('register') }}</button>
          <button class="btn-primary btn-full" v-if="step == 6" @click="router.replace('/auth/login')">{{ $t('login') }}</button>
        </div>

        <div class="auth-links" v-if="step == 6">
          <ion-text @click="step = 1; removeLocalData('registerStep'); removeLocalData('registerData')" class="link-action">{{ $t('register_again') }}</ion-text>
        </div>

        <div class="auth-links" v-if="step < 6">
          <ion-text class="link-text">{{ $t('already.account') }}</ion-text>
          <ion-text @click="router.replace('/auth/login')" class="link-action">{{ $t('login') }}</ion-text>
        </div>

        <div class="contact-link-container" v-if="step < 6">
          <ion-text @click="router.replace('/app/help')" class="link-contact">{{ $t('contact.link') }}</ion-text>
        </div>
      </div>
    </div>

    <!-- MODAL CONTACT -->
    <div v-if="viewContactModal" class="overlay-modal">
      <Contact @close="viewContactModal = false" />
    </div>

      </ion-page>
</template>

<style scoped>
ion-page {
  --background: transparent;
}

/* Logo header */
.auth-logo-header {
  display: flex;
  justify-content: center;
  padding-top: calc(env(safe-area-inset-top, 20px) + 20px);
  padding-bottom: 10px;
}

.auth-logo {
  height: 50px;
  width: auto;
}

.auth-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.auth-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 30px;
  padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 40px);
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.step-form {
  gap: 10px;
}

/* Step title */
.step-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #5A695B;
  text-align: center;
  margin: 0 0 15px 0;
}

/* Gender options */
.gender-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gender-option {
  background: #EDEAD5;
  border: 1px solid #5A695B;
  border-radius: 16px;
  padding: 20px 20px;
  min-height: 60px;
  font-size: 1rem;
  font-weight: 500;
  color: #5A695B;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gender-option.selected {
  background: #5A695B;
  color: #F8E9BF;
  border-color: #5A695B;
}

.gender-option.selected-highlight.selected {
  background: #5A695B;
  color: #F8E9BF;
}

/* Inputs */
.auth-input {
  background: #EDEAD5;
  border: 1px solid #5A695B;
  border-radius: 16px;
  padding: 0 16px;
  min-height: 52px;
  display: flex;
  align-items: center;
  position: relative;
}

.auth-input ion-input {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --placeholder-color: #FF5287;
  --placeholder-opacity: 0.6;
  --color: #5A695B;
  font-size: 1rem;
  text-align: left;
}

.input-check {
  color: #5A695B;
  font-size: 1.2rem;
  margin-left: 10px;
}

/* Verification step */
.verification-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.verification-icon {
  margin-bottom: 20px;
}

.verification-img {
  width: 80px;
  height: 80px;
  object-fit: contain;
}

.verification-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #5A695B;
  margin: 0 0 10px 0;
}

.verification-subtitle {
  font-size: 0.9rem;
  color: #5A695B;
  opacity: 0.8;
  margin: 0;
}

.code-input-container {
  margin-top: 30px;
  width: 100%;
}

.resend-container {
  margin-top: 25px;
  text-align: center;
}

.resend-hint {
  font-size: 0.85rem;
  color: #5A695B;
  display: block;
}

.resend-link {
  font-size: 0.9rem;
  color: #5A695B;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  display: block;
  margin-top: 5px;
}

/* Texts */
.hint-text {
  font-size: 0.75rem;
  color: #5A695B;
  opacity: 0.7;
  line-height: 1.4;
}

.error-text {
  font-size: 0.75rem;
  color: #e74c3c;
  margin-left: 5px;
}

/* Checkbox */
.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
}

.checkbox-label {
  font-size: 0.8rem;
  color: #5A695B;
  line-height: 1.4;
}

.policy-link {
  color: #5A695B;
  text-decoration: underline;
}

ion-checkbox {
  --size: 22px;
  --checkbox-background-checked: #FF5287;
  --checkbox-background: #FFFFFF;
  --border-color: #FF5287;
  --border-color-checked: #FF5287;
  flex-shrink: 0;
  margin-top: 2px;
}

ion-checkbox::part(container) {
  border-radius: 50%;
  border: 1px solid #FF5287;
}

/* Buttons */
.button-container {
  display: flex;
  gap: 10px;
  margin-top: 25px;
}

.btn-back {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #FF5287;
  color: #FF5287;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-back ion-icon {
  font-size: 1.3rem;
}

.btn-primary {
  flex: 1;
  background: #FF5287;
  border: none;
  border-radius: 16px;
  color: #FFFFFF;
  font-size: 1rem;
  font-weight: 600;
  padding: 15px 30px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn-primary.btn-full {
  width: 100%;
}

.btn-primary:active {
  opacity: 0.7;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Links */
.auth-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  margin-top: 20px;
}

.link-text {
  font-size: 0.85rem;
  color: #5A695B;
}

.link-action {
  font-size: 0.85rem;
  font-weight: 700;
  color: #5A695B;
  text-decoration: underline;
  cursor: pointer;
}

.contact-link-container {
  text-align: center;
  margin-top: 15px;
}

.link-contact {
  font-size: 0.85rem;
  font-weight: 700;
  color: #5A695B;
  text-decoration: underline;
  cursor: pointer;
}

/* Success */
.success-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #5A695B;
  text-align: center;
  margin: 0;
}

.success-subtitle {
  font-size: 1rem;
  color: #5A695B;
  text-align: center;
  margin-top: 20px;
}

.vertical-center {
  justify-content: center;
  align-items: center;
  min-height: 200px;
}


/* Modal */
.overlay-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: linear-gradient(to bottom, var(--ion-color-header), var(--ion-color-footer));
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Hide scrollbar */
*::-webkit-scrollbar {
  display: none !important;
}
</style>